/**
 * @fileoverview app.routes.ts
 * @description
 * **The Navigator Top-Level Route Map**
 * Eager and lazy `loadChildren` for CRM, OMS, blogs, **teacher-resources** (admin), settings, etc.
 * Teacher resources: `/teacher-resources` → `TeacherResourcesModule` (list + editor), mirroring
 * blogs’ pattern; permission guards can be enabled when `MANAGE_TEACHER_RESOURCES` is wired in IAM.
 *
 * ARCHITECTURAL PRINCIPLES:
 * 1. **Lazy feature modules** — large admin surfaces do not bloat the initial bundle.
 * 2. **Guards** — auth, school-site, and permission checks applied at the segment that needs them.
 */
import { Routes } from '@angular/router'
import { HomeComponent } from './features/home/home.component'
import { ShikshaComingSoonComponent } from './features/home/components/shiksha-coming-soon/shiksha-coming-soon'
import { authGuard } from './core/guards/auth.guard'
import { shikshaHomeGuard, shikshaHomePageGuard } from './core/guards/shiksha-home.guard'
import { cslApprovalsHomeGuard } from './core/guards/csl-approvals-home.guard'
import { createPermissionGuard } from './core/guards/permission.guard'
import { academicCalendarAccessGuard } from './core/guards/academic-calendar.guard'
import { fieldVisitAccessGuard } from './core/guards/field-visit.guard'
import { PERMISSIONS } from './core/constants/permissions.constants'
import { financeAppRoute } from './features/finance/finance.routes'
import { schoolSiteGuard } from './core/guards'
import { bannerAccessGuard } from './features/ei-banner/guards/banner-access.guard'

export const routes: Routes = [
    {
        path: '',
        component: HomeComponent,
        canActivate: [authGuard, schoolSiteGuard, shikshaHomeGuard, cslApprovalsHomeGuard],
    },
    {
        path: 'home-dashboard',
        component: HomeComponent,
        canActivate: [authGuard, schoolSiteGuard, shikshaHomeGuard, cslApprovalsHomeGuard],
    },
    {
        path: 'my-revenue',
        loadComponent: () =>
            import('./features/home/pages/my-revenue-view/my-revenue-view.component').then(
                (m) => m.MyRevenueViewComponent,
            ),
        canActivate: [
            authGuard,
            schoolSiteGuard,
            shikshaHomeGuard,
            createPermissionGuard(PERMISSIONS.VIEW_MY_REVENUE_DASHBOARD),
        ],
    },
    {
        path: 'team-revenue',
        loadComponent: () =>
            import('./features/home/pages/team-revenue-view/team-revenue-view.component').then(
                (m) => m.TeamRevenueViewComponent,
            ),
        canActivate: [
            authGuard,
            schoolSiteGuard,
            shikshaHomeGuard,
            createPermissionGuard(PERMISSIONS.VIEW_TEAM_REVENUE_DASHBOARD),
        ],
    },
    {
        path: 'sales-assist',
        loadChildren: () =>
            import('./features/sales-assist/sales-assist.module').then((m) => m.SalesAssistModule),
        canActivate: [
            authGuard,
            schoolSiteGuard,
            shikshaHomeGuard,
            createPermissionGuard(PERMISSIONS.ENABLE_SALES_ASSIST),
        ],
    },
    {
        path: 'shiksha',
        component: ShikshaComingSoonComponent,
        canActivate: [authGuard, shikshaHomePageGuard],
    },
    {
        path: 'crm',
        loadChildren: () => import('./features/crm/crm.module').then((m) => m.CrmModule),
    },
    {
        path: 'oms',
        loadChildren: () => import('./features/oms/oms.module').then((m) => m.OmsModule),
    },
    {
        path: 'school-site',
        loadChildren: () =>
            import('./features/school-site/school-site.module').then((m) => m.SchoolSiteModule),
    },
    {
        path: 'service-desk',
        loadChildren: () =>
            import('./features/service-desk/service-desk.module').then((m) => m.ServiceDeskModule),
    },
    {
        path: 'sis',
        loadChildren: () => import('./features/sis/sis.module').then((m) => m.SisModule),
        canActivate: [authGuard, createPermissionGuard(PERMISSIONS.VIEW_SIS_PORTFOLIO)],
    },
    {
        path: 'translation-management',
        loadChildren: () =>
            import('./features/translation-management/translation-management.module').then(
                (m) => m.TranslationManagementModule,
            ),
        canActivate: [createPermissionGuard(PERMISSIONS.VIEW_TRANSLATION_MANAGEMENT)],
    },
    {
        path: 'blogs',
        loadChildren: () => import('./features/blogs/blogs.module').then((m) => m.BlogsModule),
        canActivate: [authGuard, createPermissionGuard(PERMISSIONS.MANAGE_BLOGS)],
    },
    {
        path: 'teacher-resources',
        loadChildren: () =>
            import('./features/teacher-resources/teacher-resources.module').then(
                (m) => m.TeacherResourcesModule,
            ),
        canActivate: [authGuard, createPermissionGuard(PERMISSIONS.MANAGE_TEACHER_RESOURCES)],
    },
    {
        path: 'cbe-resources',
        loadChildren: () =>
            import('./features/cbe-resources/cbe-resources.module').then(
                (m) => m.CbeResourcesModule,
            ),
        canActivate: [authGuard, createPermissionGuard(PERMISSIONS.MANAGE_CBE_RESOURCES)],
    },
    {
        path: 'teams',
        loadChildren: () => import('./features/teams/teams.module').then((m) => m.TeamsModule),
        canActivate: [authGuard, createPermissionGuard(PERMISSIONS.MANAGE_TEAMS)],
    },
    {
        path: 'careers',
        loadChildren: () =>
            import('./features/careers/careers.module').then((m) => m.CareersModule),
        canActivate: [authGuard, createPermissionGuard(PERMISSIONS.MANAGE_CAREERS)],
    },
    {
        path: 'in-the-news',
        loadChildren: () =>
            import('./features/in-the-news/in-the-news.module').then((m) => m.InTheNewsModule),
        canActivate: [authGuard, createPermissionGuard(PERMISSIONS.MANAGE_NEWS)],
    },
    {
        path: 'webinars',
        loadChildren: () =>
            import('./features/webinars/webinars.module').then((m) => m.WebinarsModule),
        canActivate: [authGuard, createPermissionGuard(PERMISSIONS.MANAGE_WEBINARS)],
    },
    {
        path: 'downloads',
        loadChildren: () =>
            import('./features/downloads/downloads.module').then((m) => m.DownloadsModule),
        canActivate: [authGuard, createPermissionGuard(PERMISSIONS.MANAGE_DOWNLOADS)],
    },
    {
        path: 'academic-calendar',
        loadChildren: () =>
            import('./features/academic-calendar/academic-calendar.routes').then(
                (m) => m.academicCalendarRoutes,
            ),
        canActivate: [authGuard, academicCalendarAccessGuard],
    },
    financeAppRoute,
    {
        path: 'field-visit',
        loadChildren: () =>
            import('./features/field-visit/field-visit.routes').then((m) => m.fieldVisitRoutes),
        canActivate: [authGuard, fieldVisitAccessGuard],
    },
    {
        path: 'settings',
        loadChildren: () =>
            import('./modules/settings/settings.module').then((m) => m.SettingsModule),
        canActivate: [authGuard],
    },
    {
        path: 'mcp-analytics',
        loadComponent: () =>
            import('./features/mcp-analytics-dashboard/mcp-analytics-dashboard.component').then(
                (module) => module.McpAnalyticsDashboardComponent,
            ),
        canActivate: [authGuard, createPermissionGuard(PERMISSIONS.VIEW_MCP_DASHBOARD)],
    },
    {
        path: 'newsletters',
        loadChildren: () =>
            import('./features/newsletters/newsletters.module').then((m) => m.NewslettersModule),
        canActivate: [authGuard, createPermissionGuard(PERMISSIONS.MANAGE_NEWSLETTERS)],
    },
    {
        path: 'ei-banner',
        loadChildren: () =>
            import('./features/ei-banner/ei-banner.module').then((m) => m.EiBannerModule),
        canActivate: [authGuard, bannerAccessGuard],
    },
    { path: '**', redirectTo: '' },
]
